import React, { cloneElement, useState, useEffect, useMemo } from 'react'
import {
  Root,
  Content as DialogContent,
  Close,
  Description,
  Portal,
  Title,
} from '@radix-ui/react-dialog'
import {
  Content as CollapsibleContent,
  Root as CollapsibleRoot,
  Trigger as CollapsibleTrigger,
} from '@radix-ui/react-collapsible'
import { useHeightResize } from 'sidecar-utilities'

import {
  bodyStyles,
  collapsibleContentStyles,
  getMaxHeightStyle,
  headerStyles,
  modalStyles,
  resizeAreaStyles,
  resizeHandleStyles,
  titleStyles,
} from './styles'
import { Pill } from '@sidecar-components/atoms/Pill'
import { Alert } from '@sidecar-components/molecules/Alert'
import { Button } from '@sidecar-components/atoms/Button'
import { Icon } from '@sidecar-components/atoms/Icon'
import { IconButton } from '@sidecar-components/molecules/IconButton'
import { Menu } from '@sidecar-components/molecules/Menu'
import { PoptartProps } from './Poptart.types'

/**
 * Poptarts overlay onto the page but don't prevent the user from interacting
 * with the page still.
 *
 * @param {string} bodyClass - Adds more classes to the poptart's body section
 *   for specific use cases
 * @param {boolean} canCollapse - Whether or not the user can collapse the
 *   poptart to just the header
 * @param {JSX} children - Poptart content
 * @param {string} className - Adds more classes to the poptart for specific use
 *   cases
 * @param {string} confirmCloseTitle - Title text to confirm they want to close
 *   the poptart
 * @param {string} confirmCloseSubtitle - Subtitle text to confirm they want to
 *   close the poptart
 * @param {string} confirmCloseButton - Button copy to confirm they want to
 *   close the poptart
 * @param {JSX} description - An optional accessible description to be announced
 *   when the dialog is opened
 * @param {MenuSectionType[]} headerActions - List of options that go into the
 *   header's "more" menu
 * @param {boolean} isMaximized - Whether or not the poptart is maximized
 * @param {boolean} isOpen - The controlled open state of the dialog. Must be
 *   used in conjunction with onOpenChange.
 * @param {function} onBack - Optional function to fire when user clicks back
 *   button
 * @param {function} onClick - Optional function to fire when you click anywhere
 *   on the poptart. Mainly used to focus the poptart when there are multiple.
 * @param {function} onClose - Optional function to fire when user closes
 *   poptart
 * @param {function} onMaximizeChange - Optional function to fire when the
 *   poptart is maximized or minimized.
 * @param {function} onOpenChange - Event handler called when the open state of
 *   the dialog changes
 * @param {string} title - Text that goes in the poptart's header
 * @param {string} titleIcon - Name of the icon that goes to the left of the
 *   title
 * @param {string} titleIconClass - Adds more classes to the poptart title icon
 *   for specific use cases
 * @param {Sentiment} titlePillSentiment - Sentiment styling for the title pill
 * @param {string} titlePillText - Text to display in the pill next to the title
 * @returns {JSX} - Dialog box that can appear on top of the page
 */
export const Poptart = ({
  bodyClass = '',
  canCollapse,
  children,
  className = '',
  confirmCloseTitle = 'Are you sure you want to close',
  confirmCloseSubtitle = 'You will lose any progress you{`\u2019`}ve made.',
  confirmCloseButton = 'Close',
  testId,
  description,
  headerActions = [],
  initialHeight = 480,
  isMaximized: controlledMaximized,
  isOpen,
  onBack,
  onClick,
  onClose,
  onExpandChange,
  onMaximizeChange,
  onOpenChange,
  title,
  titleIcon,
  titleIconClass,
  titlePillSentiment,
  titlePillText,
}: PoptartProps) => {
  const [shouldConfirmClose, setShouldConfirmClose] = useState(false)
  const [isExpanded, setIsExpanded] = useState(true)
  const [height, onResizeStart, onTouchStart, onKeyboardResize, isResizing] =
    useHeightResize({
      minHeight: 200,
      maxHeight: undefined,
      initialHeight,
    })

  // Needed to track when DialogContent is actually loaded
  // for the Menu's container to use it.
  // This allows the Menu to be within the Dialog vs a sibling to it.
  const [dialogRef, setDialogRef] = useState<HTMLDivElement | null>(null)

  // In most cases, the poptart min/maximized is controlled internally via
  // the min/maximize button in the header.
  const [internalMaximized, setInternalMaximized] = useState(false)

  // In the case of the Sidekick there may be external actions that need to
  // minimize the poptart. For example if the poptart is maximized and the user
  // clicks a link in one of the responses which redirects them to a new page.
  // We would then want to minimize the poptart so they can see that page.
  const isMaximized = controlledMaximized ?? internalMaximized

  // When maximizing while collapsed, also expand the poptart
  // This prevents having just a title bar taking up the full screen
  useEffect(() => {
    if (isMaximized && !isExpanded) {
      setIsExpanded(true)
    }
  }, [isMaximized, isExpanded])

  // This handler is for managing the internal min/maximize state only. If the
  // poptart min/maximize is managed externally, we should assume that whatever
  // handler is responsible for that is also acting like onMaximizeChange would.
  const handleMaximizeToggle = () => {
    setInternalMaximized(!isMaximized)
    onMaximizeChange?.(!isMaximized)
  }

  // Handle expand/collapse changes
  // When collapsing while maximized, also minimize the poptart
  const handleExpandChange = (expanded: boolean) => {
    setIsExpanded(expanded)
    onExpandChange?.(expanded)

    // If collapsing while maximized, minimize as well
    if (!expanded && isMaximized) {
      if (controlledMaximized !== undefined) {
        // If maximize state is controlled externally, notify the parent
        onMaximizeChange?.(false)
      } else {
        // If maximize state is controlled internally, update it
        setInternalMaximized(false)
        onMaximizeChange?.(false)
      }
    }
  }

  // Memoize the test ids to avoid re-rendering the component when they don't change
  const accordionBtnTestId = useMemo(() => {
    const prefix = testId || 'btn_poptart'
    const action = isExpanded ? 'collapse' : 'expand'
    const suffix = isExpanded ? 'Collapse' : 'Expand'
    return testId ? `${prefix}_${action}` : `${prefix}${suffix}`
  }, [testId, isExpanded])

  const fullScreenBtnTestId = useMemo(() => {
    const prefix = testId || 'btn_poptart'
    const action = isMaximized ? 'exitFullScreen' : 'fullScreen'
    const suffix = isMaximized ? 'ExitFullScreen' : 'FullScreen'
    return testId ? `${prefix}_${action}` : `${prefix}${suffix}`
  }, [testId, isMaximized])

  return (
    // modal false allows user to interact with the rest of the page
    <Root modal={false} onOpenChange={onOpenChange} open={isOpen}>
      {/* The poptarts will go in #popTartWrapper which will control most of the poptart's positing */}
      <Portal
        container={
          document.querySelector('#popTartWrapper') as HTMLElement | null
        }
      >
        <DialogContent
          ref={setDialogRef}
          className={`${modalStyles(isMaximized)} ${className}`}
          style={{ maxHeight: getMaxHeightStyle({ height, isMaximized }) }}
          data-testid={testId ? `info_${testId}` : 'info_popTartContent'}
          // Explicitly set to undefined when no description to suppress Radix warning
          {...(!description && { 'aria-describedby': undefined })}
          //This prevents the dialog from closing when clicking outside of it
          onInteractOutside={e => {
            e.preventDefault()
          }}
          onClick={onClick}
        >
          <CollapsibleRoot
            onOpenChange={handleExpandChange}
            open={isExpanded}
            asChild
          >
            <>
              <div role="toolbar">
                {/* Resize handle
                We should hide the handle if the poptart is
                - minimized and only showing the title
                - maximized to the full screen */}
                {isExpanded && !isMaximized && (
                  <div
                    className={resizeAreaStyles}
                    data-testid={
                      testId
                        ? `${testId}_resizeHandle`
                        : 'btn_poptartResizeHandle'
                    }
                    onMouseDown={onResizeStart}
                    onTouchStart={onTouchStart}
                    onKeyDown={e => {
                      // Enter and Space only swallow the page scroll they would
                      // otherwise trigger on a focused separator. They deliberately
                      // do not start a resize: there is no key release to end one,
                      // so `isResizing` would latch on and the panel would follow
                      // the cursor with no button held. The arrow keys are the
                      // keyboard resize, and they need no session.
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                      }
                      onKeyboardResize(e)
                    }}
                    aria-label="Resize poptart"
                    aria-orientation="vertical"
                    role="separator"
                    tabIndex={0}
                  >
                    <div className={resizeHandleStyles} />
                  </div>
                )}

                {/* The header styles need to know if the the poptart is full screen or minimized to just the title
                to add/remove padding to compensate for the resize handle */}
                <div
                  className={headerStyles({
                    isMinimized: !isExpanded && !isMaximized,
                    isMaximized,
                  })}
                >
                  {/* Back button */}
                  {onBack && (
                    <IconButton
                      testId={testId ? `btn_${testId}_back` : 'btn_poptartBack'}
                      icon="back"
                      label="Back"
                      onClick={onBack}
                    />
                  )}

                  {/* Mainly used to show the Sidekick icon */}
                  {titleIcon && (
                    <Icon
                      name={titleIcon}
                      // negative margin brings the title a little closer
                      // without making the icon buttons closer or requiring another div
                      className={`-mr-2 ${titleIconClass}`}
                    />
                  )}

                  <Title
                    className={
                      titlePillText ? 'text-xl md:text-2xl m-0' : titleStyles
                    }
                  >
                    {title}
                  </Title>

                  {/* Pill and spacer for tags such as "Beta" */}
                  {titlePillText && (
                    <>
                      <Pill
                        text={titlePillText}
                        sentiment={titlePillSentiment}
                      />
                      {/* Spacer to push action buttons to the right */}
                      <div className="flex-1" />
                    </>
                  )}

                  {/* Button to show additional actions */}
                  {headerActions.some(section => section.items.length > 0) && (
                    <Menu
                      trigger={
                        <IconButton
                          icon="more"
                          label="Options"
                          testId={
                            testId
                              ? `btn_${testId}_moreMenu`
                              : 'btn_poptartMoreMenu'
                          }
                        />
                      }
                      sections={headerActions}
                      container={dialogRef}
                    />
                  )}

                  {/* Expand/collapse button. This will toggle showing the poptart as just the title. */}
                  {canCollapse && (
                    <CollapsibleTrigger asChild>
                      <IconButton
                        testId={accordionBtnTestId}
                        icon={isExpanded ? 'minus' : 'chevronUp'}
                        label={isExpanded ? 'Minimize' : 'Expand'}
                      />
                    </CollapsibleTrigger>
                  )}

                  {/* Maximize button. This will toggle showing the poptart as full screen */}
                  <IconButton
                    testId={fullScreenBtnTestId}
                    icon={isMaximized ? 'minimize' : 'expand'}
                    label={
                      isMaximized ? 'Exit full screen' : 'View full screen'
                    }
                    onClick={handleMaximizeToggle}
                  />

                  {/* Close button */}
                  {shouldConfirmClose ? (
                    // Sometimes (ex: member inbox) we want to make sure the user knows that
                    // closing the poptart will lose their progress
                    <Alert
                      action={
                        <Close asChild>
                          <Button
                            onClick={onClose}
                            testId="btn_confirmClosePoptart"
                          >
                            {confirmCloseButton}
                          </Button>
                        </Close>
                      }
                      title={confirmCloseTitle}
                      canCancel={true}
                      trigger={
                        <IconButton
                          testId={
                            testId ? `btn_${testId}_close` : 'btn_poptartClose'
                          }
                          icon="x"
                          label="Close"
                        />
                      }
                    >
                      {!!confirmCloseSubtitle && (
                        <p className="flex justify-center">
                          {confirmCloseSubtitle}
                        </p>
                      )}
                    </Alert>
                  ) : (
                    <Close asChild>
                      <IconButton
                        testId={
                          testId ? `btn_${testId}_close` : 'btn_poptartClose'
                        }
                        icon="x"
                        label="Close"
                        onClick={onClose}
                      />
                    </Close>
                  )}
                </div>
              </div>

              <CollapsibleContent
                className={collapsibleContentStyles(isExpanded)}
                // needed so collapsing the form doesn't clear the user's input
                forceMount={true}
              >
                {/* modal body */}
                <div
                  data-testid={testId ? `${testId}_body` : 'info_poptartBody'}
                  className={`${bodyClass} ${bodyStyles(isMaximized, isResizing)}`}
                >
                  {description && (
                    <Description asChild>{description}</Description>
                  )}
                  {/* cloneElement will allow the children to open the alert if they need to */}
                  {/* Only pass setShouldConfirmClose to React components, not DOM elements */}
                  {children &&
                    (typeof children.type === 'string'
                      ? children
                      : cloneElement(children, { setShouldConfirmClose }))}
                </div>
              </CollapsibleContent>
            </>
          </CollapsibleRoot>
        </DialogContent>
      </Portal>
    </Root>
  )
}
